import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TemplateType } from '../../../../../core/enums';
import { GeneralActionDialogComponent } from '../../crud/general-action-dialog/general-action-dialog.component';
import { QuickComment } from '../../../../../core/models/quick-comment.model';
import { QuickCommentsService } from '../../../../../core/services/quick-comments.service';
import { LayoutUtilsService, MessageType } from 'src/app/core/_base/crud';

interface FormValue {
  code: string;
  comment: string;
}

@Component({
  selector: 'kt-quick-comments',
  templateUrl: './quick-comments.component.html',
})
export class QuickCommentsComponent implements OnInit {
  TemplateType = TemplateType;
  form: FormGroup;
  index = '';
  quickComments: Partial<QuickComment>[] = [];
  private _templateId: number;
  @Input() set templateId(value: number) {
    if (!value) {
      this.quickComments = [];
      return;
    }
    this._templateId = value;
    this.loadQuickComments(value);
  }
  @Input() mode;
  constructor(
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private quickCommentsService: QuickCommentsService,
    private cdr: ChangeDetectorRef,
    private layoutUtilsService: LayoutUtilsService,
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      code: ['', Validators.required],
      comment: ['', Validators.required],
    });

    // View Mode
    if (this.mode === 'view') this.form.disable();
  }

  reset(): void {
    this.index = '';

    this.form.reset({
      code: '',
      comment: '',
    });
  }

  hasError(controlName: string, errorType: 'required' | 'duplicate'): boolean {
    const control = this.form.get(controlName);

    return control.touched && control.hasError(errorType);
  }

  getErrorMessage(controlLabel: string, errorType: 'required' | 'duplicate'): string {
    switch (errorType) {
      case 'required':
        return `${controlLabel} field is required`;
      case 'duplicate':
        return `${controlLabel} field must be unique`;
    }
  }

  mapFormToQuickComment(value: FormValue): Partial<QuickComment> {
    return {
      code: value.code,
      comment: value.comment,
    };
  }

  mapQuickCommentToForm(quickComment: Partial<QuickComment>): FormValue {
    return {
      code: quickComment.code,
      comment: quickComment.comment,
    };
  }

  addQuickComment(event: Event): void {
    event.stopPropagation();

    const formValue = this.form.value;
    let quickComments = [...this.quickComments];

    if (this.index) {
      quickComments = quickComments.map((quickComment, index) =>
        index === +this.index
          ? { ...quickComment, ...this.mapFormToQuickComment(formValue) }
          : quickComment,
      );
    } else {
      quickComments.unshift(this.mapFormToQuickComment(formValue));
    }

    if (
      quickComments.filter(
        (quickComment) => quickComment.code.trim().localeCompare(formValue.code.trim()) === 0,
      ).length > 1
    ) {
      this.form.get('code')?.setErrors({ duplicate: true });
      this.dialog.open(GeneralActionDialogComponent, {
        width: '400px',
        disableClose: false,
        closeOnNavigation: true,
        data: {
          description: `
            <div style="height: 200px" class="d-flex flex-column flex-center">
              <i style="font-size: 80px" class="flaticon-warning text-danger"></i>
              <p class="font-weight-bold text-center">Unable to add new code. Code field must be unique.</p>
            </div>
          `,
        },
      });
    }

    this.form.markAllAsTouched();

    if (this.form.invalid) return;

    this.quickComments = quickComments;
    this.updateFinalQuickComments();

    this.reset();
  }

  updateQuickComment(event: Event, idx: number): void {
    event.stopPropagation();

    const quickComment = this.quickComments.find((record, index) => index === idx);

    this.index = `${idx}`;
    this.form.patchValue(this.mapQuickCommentToForm(quickComment));
  }

  cancelQuickCommentUpdate(event: Event): void {
    event.stopPropagation();

    this.reset();
  }

  deleteQuickComment(event: Event, idx: number): void {
    event.stopPropagation();
    const _title = 'Delete Quick Comment';
    const _description = 'Are you sure you want to delete this item?';
    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, 'Loading...');
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      idx === 0 ? this.quickComments.shift() : this.quickComments.splice(idx, 1);
      this.updateFinalQuickComments();
    });
  }

  loadQuickComments(templateID) {
    if (!templateID) return;

    this.quickCommentsService.getQuickComments(templateID).subscribe((quickComments) => {
      this.quickComments = quickComments?.data || [];
      this.cdr.detectChanges();
    });
  }

  updateFinalQuickComments() {
    this.quickCommentsService
      .updateQuickComment({ quick_comment_dto_list: this.quickComments }, this._templateId)
      .subscribe(() => {
        this.loadQuickComments(this._templateId);
        this.layoutUtilsService.showActionNotification(
          'Quick Comment updated successfully',
          MessageType.Update,
          3000,
          false,
          false,
        );
      });
  }
}
